import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SharedService } from 'src/app/services/shared.service';
import {
  AppConstants,
  FiftyMB,
  TwoHundredMB,
} from 'src/app/types/constants/app.constant';
import { StatusTypes } from 'src/app/types/enums/other.enum';
import { IFileUpload, IMediaInfo } from 'src/app/types/interfaces/common.interface';
import { getThumbnailUrl, translateWithGroupPrefix } from '../../utils/helper';
import { ToastService } from '../toast/toast.service';

@Component({
  selector: 'app-upload-image-box',
  templateUrl: './upload-image-box.component.html',
  styleUrls: ['./upload-image-box.component.scss'],
})
export class UploadImageBoxComponent {
  @Input() status: string;
  mediaStatus = AppConstants.MEDIA_STATUS;

  @ViewChild('video') videoTag: ElementRef<any>;
  @Input() viewOnlyMode: boolean = false;
  @Input() isSelectable: boolean = false;
  @Input() promptText?: string;
  _selected: boolean;
  @Input()
  get selected(): boolean { return this._selected; }
  set selected(s: boolean) { this._selected = s; this.selectedChange.emit(s); }
  @Output() selectedChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output() fileChanged: EventEmitter<IMediaInfo> =
    new EventEmitter<IMediaInfo>();
  @Output() deleteHandler: EventEmitter<Event> = new EventEmitter<Event>();
  @Output() previewHandler: EventEmitter<Event> = new EventEmitter<Event>();
  @Output() customActionHandler: EventEmitter<Event> =
    new EventEmitter<Event>();

  @ViewChild('hiddenInput') hiddenInput: ElementRef;
  @Input() useHeader: string = '';
  @Input() isVideo: boolean = false;
  @Input() error?: string;
  maxSize(): number {
    return this.isVideo ? TwoHundredMB:FiftyMB
  }; // Default 10 MB

  _imgData: IMediaInfo;
  @Input()
  get media(): IMediaInfo {
    this._imgData ??= {} as IMediaInfo;
    return this._imgData;
  }

  set media(img: IMediaInfo) {
    this._imgData = img;
  }

  @Input() componentName: string;
  @Input() haveAction: boolean = true;
  @Input() useCustomAction: boolean = false;
  @Input() showVideoPlayButton: boolean = false;

  isDragging: boolean = false;
  dragEnter: boolean = false;
  getThumbnailUrl = getThumbnailUrl;
  MEDIA_STATUS = StatusTypes.MEDIA_STATUS;

  get fileTypes() {
    return this.isVideo? ['mp4', 'avi', 'mov'] : ['jpg', 'png'];
  }

  get mediaTitle() {
    return translateWithGroupPrefix(this.translateService, this.useHeader, 'MEDIA');
  }

  constructor(
    private toast: ToastService,
    private cdRef: ChangeDetectorRef,
    private translateService: TranslateService,
    private sharedService: SharedService
    ) {}

  hasFile() {
    return this.media.file_upload_id != null;
  }

  deleteFile() {
    this.media.file_upload_id = this.media.file = undefined;
  }

  downloadFile() {
    if (!this.media?.file_upload_id) return;
    this.sharedService.downloadFile(this.media.file_upload_id.id.toString()).subscribe({
      error: err => {
        this.toast.error(err.message);
      }
    })
  }

  isDownloadAvailable() {
    return this.media.file_upload_id?.id != null;
  }

  filterFileInput() {
    const formats: string[] = [...this.fileTypes];
    formats.forEach((item, index) => {
      formats[index] = `.${item}`;
    });
    return formats;
  }

  validateFile(file: File) {
    try {
      const fileExtension = file.name.split('.').pop()?.toLowerCase() as string;
      if (!this.fileTypes.includes(fileExtension)) {
        this.toast.error('UPLOAD_FILE.INVALID_TYPE', {
          types: this.fileTypes.join(',').toUpperCase(),
        });
      } else if (file.size > this.maxSize()) {
        this.toast.error('UPLOAD_FILE.TOO_LARGE', {
          size: this.isVideo ? 200 : 50,
        });
      } else {
        this.media.file = file;
        this.media.file_upload_id = {} as IFileUpload;
        this.media.file_upload_id.file_name = file.name;
        if (file) {
          // Use FileReader to read the file as a data URL
          const reader = new FileReader();

          reader.onload = (e: any) => {
            this.media.file_upload_id!.file_path = e.target.result;
            // reset error
            this.media.error = this.error = undefined;

            this.cdRef.detectChanges();

            // make sure the video is loaded after it is set
            setTimeout(() => {
              this.reloadVideo();
            }, 2);
          };

          reader.readAsDataURL(file);
        }
        this.fileChanged.emit(this.media);
      }
    } catch (err) {
      this.toast.error('UPLOAD_FILE.SYSTEM_ERROR');
    } finally {
      this.isDragging = false;
    }
  }
  handleFileInput(event: any) {
    this.validateFile(event.target.files[0]);
    event.target.value = null; //clear cache
  }

  reloadVideo() {
    if (this.videoTag?.nativeElement.load) {
      this.videoTag.nativeElement.load();
    }
  }

  handleDragOver(event: any) {
    event.preventDefault();
  }

  handleDragMove(event: any) {
    if (
      !event.relatedTarget ||
      !this.elementContains(
        event.currentTarget as HTMLElement,
        event.relatedTarget as HTMLElement
      )
    ) {
      this.isDragging = false;
    } else {
      this.isDragging = true;
    }
  }

  private elementContains(parent: HTMLElement, child: HTMLElement): boolean {
    return parent.contains(child);
  }

  handleDrop(event: any) {
    event.preventDefault();
    this.validateFile(event.dataTransfer.files[0]);
  }

  deleteClicked(): void {
    this.deleteHandler.emit();
  }

  previewClicked(): void {
    this.previewHandler.emit();
  }

  customActionClicked(): void {
    this.customActionHandler.emit();
  }
}
